import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import axiosPrivate from 'config/axiosPrivate';
import { ConfirmationModal } from 'components/shared/modal/ConfirmationModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import FilterListIcon from '@mui/icons-material/FilterList';
import TablePagination from 'components/shared/Pagination/TablePagination';
import './order.css';
import './agentia.css';
import Joyride, { STATUS, EVENTS, ACTIONS } from "react-joyride";
import 'shepherd.js/dist/css/shepherd.css';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_AGENT_IA_LIST_PAGE = '/AdminClient/AgentIAList';

const AgentIaList = ({ mode, listPath, detailsPath }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isConsultantRoute = mode === 'consultant';
  const agentIaListPath = isConsultantRoute ? listPath : ADMIN_CLIENT_AGENT_IA_LIST_PAGE;
  usePageTracking(agentIaListPath);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAgentCreationInProgress, setIsAgentCreationInProgress] = useState(false);
  const [hasLoadedAgentsSuccessfully, setHasLoadedAgentsSuccessfully] = useState(false);

  const [runAgentiaTour, setRunAgentiaTour] = useState(false);
  const [agentiaTourSteps, setAgentiaTourSteps] = useState([]);
  const [tourPending, setTourPending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [websites, setWebsites] = useState([]);
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [showHelpBanner, setShowHelpBanner] = useState(true);
  const [missionFilter, setMissionFilter] = useState(''); // '' | 'Rédaction' | 'Traduction'
  const [contentTypeFilter, setContentTypeFilter] = useState(''); // '' | 'PRODUCT' | 'GMB' | 'STORE_LOCATOR' | 'LONG_CATEGORY'
  const [contentTypeDropdownOpen, setContentTypeDropdownOpen] = useState(false);
  const contentTypeDropdownRef = useRef(null);
  const agentsPollIntervalRef = useRef(null);
  const fetchAgentsForWebsiteRef = useRef(null);

  useEffect(() => {
    document.title = "Typix - Mes agents IA";
  }, []);

  const handleAgentiaTourCallback = (data) => {
    const { status, action, type } = data;

    if (action === ACTIONS.CLOSE || type === EVENTS.OVERLAY) {
      setRunAgentiaTour(false);
      try {
        localStorage.setItem("agentia_list_guide_seen", "1");
      } catch (e) { }
      return;
    }

    // Fin / Skip
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunAgentiaTour(false);
      try {
        localStorage.setItem("agentia_list_guide_seen", "1");
      } catch (e) { }
    }
  };

  const buildAgentiaTourSteps = () => {
    const steps = [
      {
        target: "#agentia-create-new-agentia-btn",
        content: (
          <div>
            <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Créer un nouvel agent IA")}</h5>
            <p>{t("Vous pouvez ajouter un nouvel agent IA en cliquant sur Créer un nouvel agent IA selon la mission et le type de contenu souhaité")}</p>
          </div>
        ),
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#agentia-content-type-filter-btn",
        content: (
          <div>
            <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Filtrer les agents")}</h5>
            <p>{t("Utilisez ce filtre pour afficher les agents selon le type de contenu qu’ils produisent.")}</p>
          </div>
        ),
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#agentia-mission-filter-tabs",
        content: (
          <div>
            <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Onglets mission")}</h5>
            <p>{t("Filtrez vos agents pour retrouver rapidement ceux que vous voulez voir. Par exemple, vous pouvez afficher seulement les agents de Rédaction ou de Traduction.")}</p>
          </div>
        ),
        placement: "top",
        disableBeacon: true,
      },
    ];

    // Étape 4 uniquement s’il y a des cartes ffichées
    if (currentAgents.length > 0) {
      steps.push({
        target: "#agentia-first-agent-card",
        content: (
          <div>
            <h5 style={{ marginTop: 0, fontWeight: "600" }}>{t("Première carte d’agent")}</h5>
            <p>{t("Voici un exemple d’agent IA. Vous pouvez éditer ses paramètres ou le supprimer à tout moment. Chaque nouvel agent que vous créez apparaîtra ici.")}</p>
          </div>
        ),
        placement: "top",
        disableBeacon: true,
      });
    }

    return steps;
  };

  const startAgentiaTour = () => {
    if (isLoading) {
      setTourPending(true);
      return;
    }

    const stepsToUse = buildAgentiaTourSteps();
    setAgentiaTourSteps(stepsToUse);
    setRunAgentiaTour(false);
    setTimeout(() => setRunAgentiaTour(true), 0);
  };

  useEffect(() => {
    if (!tourPending) return;
    if (isLoading) return;
    setTourPending(false);
    const stepsToUse = buildAgentiaTourSteps();
    setAgentiaTourSteps(stepsToUse);
    setRunAgentiaTour(false);
    setTimeout(() => setRunAgentiaTour(true), 0);
  }, [tourPending, isLoading]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contentTypeDropdownRef.current && !contentTypeDropdownRef.current.contains(e.target)) {
        setContentTypeDropdownOpen(false);
      }
    };
    if (contentTypeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contentTypeDropdownOpen]);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isAiAgentLimitModalOpen, setIsAiAgentLimitModalOpen] = useState(false);
  const hasNoWebsiteConfigured = !isLoading && websites.length === 0;
  const hasNoAgentCreated =
    !isLoading &&
    !hasNoWebsiteConfigured &&
    hasLoadedAgentsSuccessfully &&
    !isAgentCreationInProgress &&
    agents.length === 0;

  const formatContentType = (ct) => {
    const key = String(ct || '').toUpperCase();
    switch (key) {
      case 'LONG_CATEGORY':
        return t('Page catégorie');
      case 'PRODUCT':
        return t('Fiche produit');
      case 'STORE_LOCATOR':
        return t('Store locator');
      case 'GMB':
        return t('GBP');
      default:
        return ct || '';
    }
  };

  const getContentTypeFilterLabel = (value) => {
    if (!value) return t('Filtres');
    switch (value) {
      case 'PRODUCT': return t('Fiche produit');
      case 'GMB': return t('GMB');
      case 'STORE_LOCATOR': return t('Store locator');
      case 'LONG_CATEGORY': return t('Page catégorie');
      default: return value;
    }
  };

  const CONTENT_TYPE_OPTIONS = [
    { value: 'PRODUCT', labelKey: 'Fiche produit' },
    { value: 'GMB', labelKey: 'GMB' },
    { value: 'STORE_LOCATOR', labelKey: 'Store locator' },
    { value: 'LONG_CATEGORY', labelKey: 'Page catégorie' },
  ];

  // Keep hostname + path so sibling sites on the same domain stay distinct
  // e.g. samsung.com/fr/smartphones vs samsung.com/fr/business
  const normalizeUrl = (url) => {
    return (url || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  };

  const findWebsiteByUrl = (websiteUrl) => {
    if (!websiteUrl || websites.length === 0) return null;

    const normalizedSelected = normalizeUrl(websiteUrl);

    const exactMatch = websites.find((w) => {
      const wUrl = w.urlSite || w.url || '';
      return wUrl === websiteUrl || normalizeUrl(wUrl) === normalizedSelected;
    });
    if (exactMatch) return exactMatch;

    // Fallback: name match only if name looks like a URL/path, not a display label
    return websites.find((w) => {
      const wName = w.name || '';
      if (!wName || !/[./]/.test(wName)) return false;
      return wName === websiteUrl || normalizeUrl(wName) === normalizedSelected;
    }) || null;
  };

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const websitesResponse = await axiosPrivate.get('/project/website/all');
        const websitesData = websitesResponse.data || [];
        setWebsites(websitesData);
      } catch (error) {
        console.error('Erreur lors du chargement des sites web:', error);
        setWebsites([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWebsites();
  }, []);

  const fromBase64 = (b64, mime = 'image/png') => `data:${mime};base64,${b64}`;

  const fromBytes = (bytes, mime = 'image/png') => {
    try {
      const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
      const blob = new Blob([u8], { type: mime });
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  };

  const inferImageUrl = (agent) => {
    const mime = agent.mimeType || 'image/png';

    if (agent.imageBase64) {
      return fromBase64(agent.imageBase64, mime);
    }
    if (agent.image && typeof agent.image === 'string' && /^[A-Za-z0-9+/=]+$/.test(agent.image)) {
      return fromBase64(agent.image, mime);
    }
    if (agent.imageBytes || Array.isArray(agent.image)) {
      return fromBytes(agent.imageBytes || agent.image, mime);
    }
    return null;
  };

  const resolveAgentType = (agent) => {
    const missionCandidates = [
      agent?.mission,
      agent?.missionType,
      agent?.typeMission,
      agent?.agentType,
      agent?.typeAgent,
      agent?.agentKind,
      agent?.type,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);

    if (missionCandidates.some((value) => value.includes('trad') || value.includes('translat'))) {
      return t('Traduction');
    }

    if (missionCandidates.some((value) => value.includes('redac') || value.includes('writ'))) {
      return t('Rédaction');
    }

    const hasRedactionSpecificFields = Boolean(
      agent?.role ||
      agent?.tonality ||
      agent?.audience ||
      agent?.redactionRules ||
      agent?.productType ||
      agent?.resultStructure ||
      agent?.bannedExpressions ||
      agent?.recommendedLexicons ||
      agent?.urls
    );

    if (hasRedactionSpecificFields) {
      return t('Rédaction');
    }

    return t('Traduction');
  };

  const mapAgentFromApi = (agent, fallbackWebsiteName, fallbackWebsiteUrl) => {
    const imageUrl = inferImageUrl(agent);

    return {
      ...agent,
      contentType: agent.typeContenu || agent.contentType || '',
      agentType: resolveAgentType(agent),
      websiteName: agent.websiteName || fallbackWebsiteName,
      websiteUrl: agent.websiteUrl || fallbackWebsiteUrl,
      language: agent.language || agent.langue || '',
      imageUrl: imageUrl || agent.imageUrl,
    };
  };

  const clearAgentsPollInterval = () => {
    if (agentsPollIntervalRef.current) {
      clearInterval(agentsPollIntervalRef.current);
      agentsPollIntervalRef.current = null;
    }
  };

  const fetchAgentsForWebsite = async (websiteUrl, options = {}) => {
    const { silent = false } = options;

    if (!websiteUrl) {
      clearAgentsPollInterval();
      setIsAgentCreationInProgress(false);
      setHasLoadedAgentsSuccessfully(false);
      setAgents([]);
      return;
    }

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setHasLoadedAgentsSuccessfully(false);

      const website = findWebsiteByUrl(websiteUrl);

      const response = await axiosPrivate.get(
        `/smart-content/agent_ia/all?websiteUrl=${encodeURIComponent(websiteUrl)}`
      );

      if (response.status === 202) {
        setIsAgentCreationInProgress(true);
        setHasLoadedAgentsSuccessfully(false);
        setAgents([]);
        if (!agentsPollIntervalRef.current) {
          agentsPollIntervalRef.current = setInterval(() => {
            const fn = fetchAgentsForWebsiteRef.current;
            if (fn) {
              fn(websiteUrl, { silent: true });
            }
          }, 10000);
        }
        return;
      }

      clearAgentsPollInterval();
      setIsAgentCreationInProgress(false);
      setHasLoadedAgentsSuccessfully(response.status === 200);

      const websiteName = website ? (website.urlSite || website.name) : websiteUrl;
      const agentsData = Array.isArray(response.data) ? response.data : [];

      setAgents(
        agentsData.map((agent) => mapAgentFromApi(agent, websiteName, websiteUrl))
      );
    } catch (error) {
      console.error(`Erreur lors du chargement des agents pour le website ${websiteUrl}:`, error);
      clearAgentsPollInterval();
      setIsAgentCreationInProgress(false);
      setHasLoadedAgentsSuccessfully(false);
      setAgents([]);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  fetchAgentsForWebsiteRef.current = fetchAgentsForWebsite;

  useEffect(() => {
    clearAgentsPollInterval();

    if (!selectedWebsiteUrl || websites.length === 0) {
      setIsAgentCreationInProgress(false);
      setHasLoadedAgentsSuccessfully(false);
      setAgents([]);
      setIsLoading(false);
      return;
    }

    // Send the exact selected URL from localStorage. Remapping through the
    // websites list can pick a sibling site on the same domain.
    fetchAgentsForWebsite(selectedWebsiteUrl);
  }, [selectedWebsiteUrl, websites]);

  useEffect(() => () => clearAgentsPollInterval(), []);

  useEffect(() => {
    return () => {
      agents.forEach(a => {
        if (a?.imageUrl?.startsWith('blob:')) {
          try { URL.revokeObjectURL(a.imageUrl); } catch { }
        }
      });
    };
  }, [agents]);

  const handleCreateAgent = async () => {
    if (isAgentCreationInProgress || websites.length === 0) {
      return;
    }
    try {
      const matchingWebsite = findWebsiteByUrl(selectedWebsiteUrl);

      const websiteId = matchingWebsite?.id;

      if (websiteId) {
        const { data } = await axiosPrivate.get(`/project/website-quotas/${websiteId}`);
        const maxAiAgents = Number(data?.maxAiAgents);
        const currentAiAgents = Number(data?.currentAiAgents);
        const hasQuotaValues = Number.isFinite(maxAiAgents) && Number.isFinite(currentAiAgents);
        const canCreateAgent = !hasQuotaValues || (maxAiAgents - currentAiAgents > 0);

        if (!canCreateAgent) {
          setIsAiAgentLimitModalOpen(true);
          return;
        }
      }

      localStorage.removeItem('agentId');
      localStorage.removeItem('agentMissionType');
      history.push(isConsultantRoute ? detailsPath : '/AdminClient/createagentiafromlist');
    } catch (error) {
      localStorage.removeItem('agentId');
      localStorage.removeItem('agentMissionType');
      history.push(isConsultantRoute ? detailsPath : '/AdminClient/createagentiafromlist');
    }
  };

  const handleEditAgent = (agentId) => {
    const agent = agents.find(a => a.id === agentId);

    if (agent && agent.agentType === t('Traduction')) {
      localStorage.setItem('agentMissionType', t('Traduction'));
    } else {
      localStorage.setItem('agentMissionType', t('Rédaction'));
    }
    localStorage.setItem('agentId', agentId);

    history.push(isConsultantRoute ? detailsPath : '/AdminClient/createagentiafromlist');
  };

  const handleDeleteAgent = (agentId) => {
    setAgentToDelete(agentId);
    setShowDeleteModal(true);
  };

  const handleGoToAddWebsite = () => {
    history.push('/AdminClient/AjouterSiteWeb');
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;

    const deletedAgent = agents.find(a => a.id === agentToDelete);
    const agentName = deletedAgent?.name || '';

    try {
      await axiosPrivate.delete(`/smart-content/agent_ia/${agentToDelete}`, {
        headers: {
          'X-OpenAI-Api-Key': '',
        },
      });

      setShowDeleteModal(false);
      setAgentToDelete(null);

      if (selectedWebsiteUrl) {
        await fetchAgentsForWebsite(selectedWebsiteUrl);
      } else {
        setAgents([]);
      }

      setTimeout(() => {
        setIsSuccess(true);
        const successMessage = agentName
          ? `L'agent IA "${agentName}" ${t('a été supprimé avec succès')} !`
          : `L'agent IA ${t('a été supprimé avec succès')} !`;
        setErrorSuccessMsg(successMessage);
        setErrorSuccess(true);
      }, 100);
    } catch (error) {
      console.error(t('Erreur lors de la suppression:'), error);
      setShowDeleteModal(false);
      setAgentToDelete(null);

      setTimeout(() => {
        setIsSuccess(false);
        setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
        setErrorSuccess(true);
      }, 100);
    }
  };

  const cancelDeleteAgent = () => {
    setShowDeleteModal(false);
    setAgentToDelete(null);
  };

  // --- FILTRES ---
  const filteredAgents = agents.filter(agent => {
    if (missionFilter && agent.agentType !== missionFilter) return false;
    if (contentTypeFilter) {
      const agentCt = String(agent.contentType || '').toUpperCase().replace(/-/g, '_');
      const wanted = contentTypeFilter.toUpperCase();
      if (agentCt !== wanted) return false;
    }
    const term = searchTerm.toLowerCase();
    return (
      agent.name?.toLowerCase().includes(term) ||
      agent.contentType?.toLowerCase().includes(term) ||
      agent.role?.toLowerCase().includes(term) ||
      agent.agentType?.toLowerCase().includes(term) ||
      agent.websiteName?.toLowerCase().includes(term)
    );
  });

  // --- TRI ---
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedAgents = [...filteredAgents].sort((a, b) => {
    if (!sortField) return 0;
    const av = (a?.[sortField] || '').toString().toLowerCase();
    const bv = (b?.[sortField] || '').toString().toLowerCase();
    const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  // --- PAGINATION ---
  const totalPages = Math.ceil(sortedAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAgents = sortedAgents.slice(startIndex, endIndex);

  return (
    <div className="content-center">
      <Joyride
        steps={agentiaTourSteps}
        run={runAgentiaTour}
        continuous
        showSkipButton
        spotlightClicks={true}
        spotlightPadding={5}
        disableOverlayClose={false}
        disableCloseOnEsc={false}
        callback={handleAgentiaTourCallback}
        styles={{
          options: {
            zIndex: 1000,
            primaryColor: "#0064d2",
          },
          overlay: {
            zIndex: 1000,
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
          spotlight: {
            zIndex: 1001,
            borderRadius: 8,
            boxShadow: "0 0 0 3px rgba(0, 100, 210, 0.25)",
          },
          tooltip: {
            zIndex: 1002,
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            width: "auto",
            maxWidth: "none",
            display: "inline-block",
          },
          tooltipContainer: {
            textAlign: "left",
          },
          tooltipTitle: {
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 8,
          },
          buttonNext: {
            borderRadius: 999,
            padding: "6px 14px",
            fontWeight: 600,
            fontSize: 13,
          },
          buttonBack: {
            borderRadius: 999,
            padding: "6px 10px",
            fontSize: 12,
          },
          buttonSkip: {
            fontSize: 12,
          },
        }}
        locale={{
          last: t("Terminer"),
          next: t("Suivant"),
          back: t("Précédent"),
          skip: t("Passer"),
          close: t("Fermer"),
        }}
      />

      {/* --- HEADER --- */}
      <div className="box-info-header agentia-header">
        <div className="b-child-1 d-flex align-items-center" style={{ gap: '12px' }}>
          <h1 className="mb-0">{t('Mes agents IA')}</h1>
          <div className="agentia-header-filters">
            {/* Filtres */}
            <div className="d-flex align-items-center gap-2 mission-filter" id="agentia-mission-filter-tabs">
              <button
                className={`agent-dashboard-badge badge-yellow ${missionFilter === '' ? 'active' : ''}`}
                onClick={() => { setMissionFilter(''); setCurrentPage(1); }}
              >
                {t('Tous')}
              </button>
              <button
                className={`agent-dashboard-badge badge-green ${missionFilter === t('Rédaction') ? 'active' : ''}`}
                onClick={() => { setMissionFilter(t('Rédaction')); setCurrentPage(1); }}
              >
                {t('Rédaction')}
              </button>
              <button
                className={`agent-dashboard-badge badge-blue ${missionFilter === t('Traduction') ? 'active' : ''}`}
                onClick={() => { setMissionFilter(t('Traduction')); setCurrentPage(1); }}
              >
                {t('Traduction')}
              </button>
            </div>
          </div>
        </div>
        <div className="b-child-2 d-flex justify-content-end align-items-center">


          <div className="agentia-header-right">
            <div className="agentia-content-type-filter" ref={contentTypeDropdownRef}>
              <button
                type="button"
                className="agentia-filtres-button"
                id="agentia-content-type-filter-btn"
                onClick={() => setContentTypeDropdownOpen((o) => !o)}
                aria-label={t('Filtrer par type de contenu')}
                aria-expanded={contentTypeDropdownOpen}
              >
                <FilterListIcon sx={{ fontSize: 20 }} className="agentia-filtres-icon" />
                <span>{getContentTypeFilterLabel(contentTypeFilter)}</span>
              </button>
              {contentTypeDropdownOpen && (
                <div className="agentia-content-type-dropdown">
                  <button
                    type="button"
                    className={`agentia-content-type-option ${!contentTypeFilter ? 'active' : ''}`}
                    onClick={() => {
                      setContentTypeFilter('');
                      setCurrentPage(1);
                      setContentTypeDropdownOpen(false);
                    }}
                  >
                    {t('Tous')}
                  </button>
                  {CONTENT_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`agentia-content-type-option ${contentTypeFilter === opt.value ? 'active' : ''}`}
                      onClick={() => {
                        setContentTypeFilter(opt.value);
                        setCurrentPage(1);
                        setContentTypeDropdownOpen(false);
                      }}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="agentia-search-wrap">
              <input
                type="text"
                className="agentia-search-input"
                placeholder={t('Rechercher un agent...')}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <button
              type="button"
              onClick={handleCreateAgent}
              className="agentia-create-btn"
              id="agentia-create-new-agentia-btn"
              disabled={isAgentCreationInProgress || websites.length === 0}
              aria-disabled={isAgentCreationInProgress || websites.length === 0}
            >
              + {t('Créer un nouvel agent AI')}
            </button>
          </div>
        </div>
      </div>

      <hr
        style={{
          border: 'none',
          borderBottom: '1px solid rgb(179, 180, 182)',
          marginLeft: '-30px',
          marginBottom: 8,
          width: 'calc(100% + 40px)',
        }}
      />

      {showHelpBanner && !isConsultantRoute && !hasNoWebsiteConfigured && !hasNoAgentCreated && !isAgentCreationInProgress && (
        <div className="agentia-help-banner" role="note" aria-label={t("Mon guide")}>
          <div className="agentia-help-banner-left">
            <span className="agentia-help-banner-icon" aria-hidden="true">?</span>
            <span className="agentia-help-banner-text">
              {t("Vous avez besoin d'aide pour utiliser cette interface ? Consultez notre guide complet pour créer et configurer vos agents IA.")}
            </span>
          </div>
          <div className="agentia-help-banner-actions">
            <button
              type="button"
              className="agentia-help-banner-link"
              onClick={startAgentiaTour}
            >
              {t("Voir le guide")}
            </button>
            <button
              type="button"
              className="agentia-help-banner-close"
              aria-label={t("Fermer")}
              title={t("Fermer")}
              onClick={() => setShowHelpBanner(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {hasNoWebsiteConfigured && (
        <div className="agentia-help-banner" role="note">
          <div className="agentia-help-banner-left">

            <span className="agentia-help-banner-text">
              {t('Aucun agent disponible — veuillez d’abord ajouter un siteweb.')}
            </span>
          </div>
          {!isConsultantRoute && (
            <div className="agentia-help-banner-actions">
              <button
                type="button"
                onClick={handleGoToAddWebsite}
                className="agentia-help-banner-link"
              >
                {t('Ajouter un siteweb')}
              </button>
            </div>
          )}
        </div>
      )}

      {hasNoAgentCreated && (
        <div className="agentia-help-banner" role="note">
          <div className="agentia-help-banner-left">

            <span className="agentia-help-banner-text">
              {t('Aucun agent IA trouvé. Créez votre premier agent pour commencer.')}
            </span>
          </div>
          <div className="agentia-help-banner-actions">
            <button
              type="button"
              onClick={handleCreateAgent}
              className="agentia-help-banner-link"
            >
              {t('Créer un nouvel agent AI')}
            </button>
          </div>
        </div>
      )}

      {isAgentCreationInProgress && (
        <div className="agentia-help-banner" role="note">
          <div className="agentia-help-banner-left">
            <span className="agentia-help-banner-text">
              {t('La création automatique de votre agent est en cours... veuillez patienter.')}
            </span>
          </div>
        </div>
      )}

      {/* --- GRILLE DE CARTES --- */}
      {!hasNoWebsiteConfigured && !hasNoAgentCreated && !isAgentCreationInProgress && (
        <div className="agent-dashboard-grid">
          {isLoading ? (
            <div className="text-center w-100">
              <div className="spinner-border mt-5" role="status">
                <span className="visually-hidden">{t('Chargement...')}</span>
              </div>
            </div>
          ) : sortedAgents.length === 0 && hasLoadedAgentsSuccessfully ? (
            <div className="text-center w-100 mt-5">
              <p className="text-muted">{t('Aucun agent IA trouvé.')}</p>
            </div>
          ) : sortedAgents.length === 0 ? null : (
            <>
              {/* Agents */}
              {currentAgents.map((agent, idx) => (
                <div
                  key={agent.id}
                  className="agent-dashboard-card"
                  id={idx === 0 ? "agentia-first-agent-card" : undefined}
                  onClick={() => handleEditAgent(agent.id)}
                >
                  <div className="agent-dashboard-actions" onClick={(e) => e.stopPropagation()}>
                    <img
                      src="Images/icon-edit.png"
                      alt="Modifier"
                      className="agent-dashboard-icon"
                      onClick={() => handleEditAgent(agent.id)}
                    />
                    {!isConsultantRoute && (
                      <img
                        src="Images/icon-trash.png"
                        alt="Supprimer"
                        className="agent-dashboard-icon"
                        onClick={() => handleDeleteAgent(agent.id)}
                      />
                    )}
                  </div>
                  <span
                    className={`agent-dashboard-badge1 ${agent.agentType === t('Traduction') ? 'badge-blue' : 'badge-green'
                      }`}
                  >
                    {agent.agentType}
                  </span>
                  <div className="agent-dashboard-header">

                    <img
                      src={agent.imageUrl || 'Images/agentIA.png'}
                      alt={agent.name}
                      className="agent-dashboard-avatar"
                    />
                    <div>
                      <h3 className="agent-dashboard-name">{agent.name}</h3>
                      {agent.agentType !== t('Traduction') && (
                        <p className="agent-dashboard-role-text">
                          {agent.role || '—'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="agent-dashboard-body">
                    <p className="agent-dashboard-meta-line">
                      {agent.agentType === t('Traduction') ? (
                        <>
                          {formatContentType(agent.contentType) && <span>{formatContentType(agent.contentType)}</span>}
                          {formatContentType(agent.contentType) && agent.language && <span className="agent-dashboard-meta-sep"> • </span>}
                          {agent.language && <span>{agent.language}</span>}
                        </>
                      ) : (
                        <>
                          <span>{formatContentType(agent.contentType) || '—'}</span>
                          <span className="agent-dashboard-meta-sep"> • </span>
                          <span>{agent.language || '—'}</span>
                        </>
                      )}
                    </p>
                    <p>
                      {agent.websiteName || '—'}
                    </p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}


      {sortedAgents.length > 15 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
          onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disablePrev={currentPage <= 1}
          disableNext={currentPage >= totalPages || totalPages <= 0}
        />
      )}

      <ConfirmationModal
        open={showDeleteModal}
        message={t('Êtes-vous sûr de vouloir supprimer cet agent IA ?')}
        handleClose={cancelDeleteAgent}
        executeAction={confirmDeleteAgent}
      />

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
      <QuitProjectCreationModal
        isOpen={isAiAgentLimitModalOpen}
        onClose={() => setIsAiAgentLimitModalOpen(false)}
        variant="enrichment"
        showCloseButton={false}
        titleIcon="/Images/black-magic-icon.png"
        title={t('Limite d’agents IA atteinte')}
        message={
          <>
            {t('Vous avez atteint le nombre maximum d’agents IA autorisés sur votre forfait.')}
            {' '}
            <strong>{t('Passez à une offre supérieure')}</strong>
            {' '}
            {t('pour créer davantage d’agents et')}
            {' '}
            <strong>{t('automatiser votre production')}</strong>
            .
          </>
        }
        showSuggestion={false}
        primaryButtonLabel={isConsultantRoute ? t('Fermer') : t('Passer au forfait superieur')}
        secondaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => {
          setIsAiAgentLimitModalOpen(false);
          if (!isConsultantRoute) {
            history.push('/AdminClient/Forfait');
          }
        }}
        onSecondaryAction={() => setIsAiAgentLimitModalOpen(false)}
      />
    </div>
  );
};

export default AgentIaList;